// Local-only Trip repository implementation using localforage
// This is the default implementation that works without any backend

import { Trip } from '@/types/trip';
import { TripRepository } from './types';
import { ensureIds, stripCalculated } from '@/utils/tripsManager';
import {
  getItem,
  setItem,
  removeItem,
  broadcastStorageChange,
  dispatchStorageSignal,
} from '@/lib/storage';

const TRIPS_KEY = 'trips:v2';
const LEGACY_TRIPS_KEY = 'trips:v1';
const TRIPS_SIGNAL_KEY = 'wsc:trips:updated';

class LocalTripRepository implements TripRepository {
  async getAll(): Promise<Trip[]> {
    if (typeof window === 'undefined') {
      return [];
    }
    try {
      const stored = await getItem<Trip[]>(TRIPS_KEY);
      if (stored && Array.isArray(stored)) {
        return ensureIds(stored);
      }

      // Check for legacy data and migrate
      const legacy = await getItem<any[]>(LEGACY_TRIPS_KEY);
      if (legacy && Array.isArray(legacy)) {
        const { migrateTripsFromV1, CURRENT_VERSION } = await import('@/utils/migrations');
        const migrated = migrateTripsFromV1(legacy);
        try {
          await setItem(TRIPS_KEY, migrated);
          await setItem('version', CURRENT_VERSION);
        } catch (e) {
          // ignore save errors
        }
        return ensureIds(migrated);
      }

      return [];
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('LocalTripRepository.getAll: failed to read trips', e);
      }
      return [];
    }
  }

  async getById(id: string): Promise<Trip | null> {
    const all = await this.getAll();
    return all.find((t) => 'id' in t && t.id === id) ?? null;
  }

  async create(trip: Trip): Promise<Trip> {
    const all = await this.getAll();
    const withId = ensureIds([trip])[0];
    const updated = [...all, withId];
    await this._persist(updated);
    return withId;
  }

  async update(id: string, trip: Trip): Promise<Trip> {
    const all = await this.getAll();
    const index = all.findIndex((t) => 'id' in t && t.id === id);
    if (index === -1) {
      throw new Error(`Trip with id ${id} not found`);
    }
    const updated = [...all];
    updated[index] = { ...trip, id } as Trip;
    await this._persist(updated);
    return updated[index];
  }

  async remove(id: string): Promise<void> {
    const all = await this.getAll();
    const filtered = all.filter((t) => !('id' in t && t.id === id));
    await this._persist(filtered);
  }

  async clear(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      await removeItem(TRIPS_KEY);
      await removeItem(LEGACY_TRIPS_KEY);
      broadcastStorageChange(TRIPS_SIGNAL_KEY);
      dispatchStorageSignal(TRIPS_SIGNAL_KEY);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('LocalTripRepository.clear: failed', e);
      }
    }
  }

  private async _persist(trips: Trip[]): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const normalized = ensureIds(trips);
      const stripped = stripCalculated(normalized);
      await setItem(TRIPS_KEY, stripped);
      broadcastStorageChange(TRIPS_SIGNAL_KEY);
      dispatchStorageSignal(TRIPS_SIGNAL_KEY);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('LocalTripRepository._persist: failed', e);
      }
      throw e;
    }
  }
}

// Export singleton instance
export const localTripRepository = new LocalTripRepository();
